/**
 * Insights Dashboard illustration — miniature wireframe of the insights page:
 * stat cards row at top, bar chart area, accuracy ring.
 */
import type { IllustrationProps } from './types';
import { usePalette } from './types';

export function InsightsDashboard({
  variant = 'on-gradient',
  className,
  style,
}: IllustrationProps) {
  const p = usePalette(variant);

  return (
    <svg
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      aria-hidden="true"
    >
      {/* Card frame */}
      <rect
        x="4"
        y="4"
        width="112"
        height="112"
        rx="10"
        fill={p.frameFill}
        stroke={p.frameStroke}
        strokeWidth="1"
      />

      {/* Stat cards row — 3 mini cards */}
      <rect
        x="14"
        y="14"
        width="27"
        height="20"
        rx="4"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      <rect x="18" y="18" width="14" height="3" rx="1.5" fill={p.accent1} opacity="0.6" />
      <rect x="18" y="24" width="10" height="2" rx="1" fill={p.faint} />

      <rect
        x="46"
        y="14"
        width="27"
        height="20"
        rx="4"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      <rect x="50" y="18" width="12" height="3" rx="1.5" fill={p.accent2} opacity="0.6" />
      <rect x="50" y="24" width="16" height="2" rx="1" fill={p.faint} />

      <rect
        x="78"
        y="14"
        width="28"
        height="20"
        rx="4"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      <rect x="82" y="18" width="16" height="3" rx="1.5" fill={p.accent3} opacity="0.6" />
      <rect x="82" y="24" width="12" height="2" rx="1" fill={p.faint} />

      {/* Bar chart — weekly activity */}
      <rect
        x="14"
        y="42"
        width="60"
        height="64"
        rx="5"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />

      {/* Chart label */}
      <rect x="19" y="47" width="24" height="2.5" rx="1.25" fill={p.faint} />

      {/* Y-axis gridlines */}
      <line x1="19" y1="60" x2="69" y2="60" stroke={p.faint} strokeWidth="0.5" />
      <line x1="19" y1="72" x2="69" y2="72" stroke={p.faint} strokeWidth="0.5" />
      <line x1="19" y1="84" x2="69" y2="84" stroke={p.faint} strokeWidth="0.5" />
      <line x1="19" y1="96" x2="69" y2="96" stroke={p.faint} strokeWidth="0.5" />

      {/* Bars — Mon through Sun */}
      <rect x="21" y="74" width="5" height="22" rx="1.5" fill={p.accent1} opacity="0.5" />
      <rect x="28" y="65" width="5" height="31" rx="1.5" fill={p.accent1} opacity="0.5" />
      <rect x="35" y="78" width="5" height="18" rx="1.5" fill={p.accent1} opacity="0.5" />
      <rect x="42" y="58" width="5" height="38" rx="1.5" fill={p.accent1} opacity="0.7" />
      <rect x="49" y="70" width="5" height="26" rx="1.5" fill={p.accent1} opacity="0.5" />
      <rect x="56" y="82" width="5" height="14" rx="1.5" fill={p.accent1} opacity="0.35" />
      <rect x="63" y="86" width="5" height="10" rx="1.5" fill={p.faint} />

      {/* Day labels */}
      <rect x="22" y="99" width="3" height="1.5" rx="0.75" fill={p.faint} />
      <rect x="29" y="99" width="3" height="1.5" rx="0.75" fill={p.faint} />
      <rect x="36" y="99" width="3" height="1.5" rx="0.75" fill={p.faint} />
      <rect x="43" y="99" width="3" height="1.5" rx="0.75" fill={p.faint} />
      <rect x="50" y="99" width="3" height="1.5" rx="0.75" fill={p.faint} />
      <rect x="57" y="99" width="3" height="1.5" rx="0.75" fill={p.faint} />
      <rect x="64" y="99" width="3" height="1.5" rx="0.75" fill={p.faint} />

      {/* Accuracy ring — right side */}
      <rect
        x="78"
        y="42"
        width="28"
        height="36"
        rx="5"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      {/* Ring */}
      <circle cx="92" cy="55" r="9" fill="none" stroke={p.faint} strokeWidth="2.5" />
      <circle
        cx="92"
        cy="55"
        r="9"
        fill="none"
        stroke={p.accent2}
        strokeWidth="2.5"
        opacity="0.6"
        strokeDasharray="40 17"
        strokeLinecap="round"
        transform="rotate(-90 92 55)"
      />
      {/* Percentage text */}
      <rect x="87" y="54" width="10" height="3" rx="1.5" fill={p.medium} />
      {/* Label */}
      <rect x="83" y="68" width="18" height="2" rx="1" fill={p.faint} />

      {/* Streak card — bottom right */}
      <rect
        x="78"
        y="82"
        width="28"
        height="24"
        rx="5"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      {/* Flame icon placeholder */}
      <circle cx="86" cy="90" r="3" fill={p.accent1} opacity="0.4" />
      {/* Number */}
      <rect x="92" y="88" width="10" height="4" rx="2" fill={p.accent1} opacity="0.5" />
      {/* Label */}
      <rect x="83" y="97" width="18" height="2" rx="1" fill={p.faint} />
    </svg>
  );
}
